import React from 'react';
import './About.css';

export default function About() {
  return (
    <main className="about-page">
      <div className="page-hero">
        <div className="container">
          <div className="hero-badge">About Us</div>
          <h1>Built for Keyboard Enthusiasts, <span className="glow-text">By Keyboard Enthusiasts</span></h1>
          <p>KeyTest is a free, privacy-first online keyboard diagnostic tool trusted by thousands of users worldwide every day.</p>
        </div>
      </div>

      <div className="container">
        <div className="about-grid">
          <div className="about-main">
            <section className="about-section">
              <h2>Our Mission</h2>
              <p>KeyTest was created with a single goal: give every computer user an instant, accurate, and completely free way to test their keyboard — no downloads, no sign-ups, no hidden costs. Whether you're a gamer verifying your mechanical board before a tournament, a writer diagnosing a stuck key, or a technician testing a refurbished device, KeyTest gives you the answers you need in seconds.</p>
              <p>We believe diagnostic tools should be accessible to everyone. That's why KeyTest has always been, and will always remain, free to use.</p>
            </section>

            <section className="about-section">
              <h2>How We Built It</h2>
              <p>KeyTest is built on modern web technologies — React for the interface, and the browser's native <code>KeyboardEvent</code> API for accurate, real-time key detection. We deliberately chose a browser-based approach for several reasons:</p>
              <ul>
                <li><strong>No installation required:</strong> You shouldn't need to install software just to check if your keyboard works.</li>
                <li><strong>Cross-platform compatibility:</strong> Works identically on Windows, macOS, and Linux without platform-specific builds.</li>
                <li><strong>Privacy by design:</strong> Browser-based processing means your keystrokes never leave your device. We have no ability to log or store your input.</li>
                <li><strong>Always up to date:</strong> You always use the latest version automatically, with no manual updates needed.</li>
              </ul>
            </section>

            <section className="about-section">
              <h2>Privacy Is Not Optional</h2>
              <p>A keyboard testing tool that secretly logs your keystrokes would be a keylogger. We are not that, and we never will be. KeyTest is engineered from the ground up so that no keystroke data can be transmitted externally — the code that handles key events runs entirely in your browser's isolated JavaScript environment and has no network access to any backend server for keypress data.</p>
              <p>We don't use cookies to track your behavior. We don't run ad networks that profile your activity. We collect only anonymous, aggregate analytics (page views, device types) to understand how to improve the tool — never individual keystroke data.</p>
            </section>

            <section className="about-section">
              <h2>Who Uses KeyTest</h2>
              <div className="user-grid">
                <div className="user-card">
                  <span className="user-icon">🎮</span>
                  <h4>Gamers</h4>
                  <p>Verify anti-ghosting, test rollover, and confirm every key responds before competitive play.</p>
                </div>
                <div className="user-card">
                  <span className="user-icon">✍️</span>
                  <h4>Writers & Typists</h4>
                  <p>Diagnose inconsistent key registration that causes missed characters during long sessions.</p>
                </div>
                <div className="user-card">
                  <span className="user-icon">💻</span>
                  <h4>Developers</h4>
                  <p>Test mechanical keyboards for chatter, verify special keys, and confirm hotkey inputs work correctly.</p>
                </div>
                <div className="user-card">
                  <span className="user-icon">🔧</span>
                  <h4>Technicians</h4>
                  <p>Quickly QA-test refurbished or repaired keyboards before returning them to users.</p>
                </div>
                <div className="user-card">
                  <span className="user-icon">🛒</span>
                  <h4>Buyers & Sellers</h4>
                  <p>Verify used keyboard condition before purchase or confirm full functionality before listing.</p>
                </div>
                <div className="user-card">
                  <span className="user-icon">🏢</span>
                  <h4>IT Departments</h4>
                  <p>Batch-test keyboards during hardware audits without installing additional diagnostic software.</p>
                </div>
              </div>
            </section>
          </div>

          <aside className="about-sidebar">
            <div className="sidebar-card">
              <h4>Quick Facts</h4>
              <ul className="fact-list">
                <li><span>🌍</span> Available in 20+ languages</li>
                <li><span>⌨️</span> Supports 104-key standard layout</li>
                <li><span>🔒</span> Zero keystroke logging</li>
                <li><span>💸</span> 100% free, no account needed</li>
                <li><span>📱</span> Works on desktop & mobile</li>
                <li><span>🚀</span> No installation required</li>
              </ul>
            </div>
            <div className="sidebar-card accent-card">
              <h4>Have a question?</h4>
              <p>We're always happy to hear from users. If you've found a bug, have a feature request, or just want to say hello — reach out.</p>
              <a href="/contact" className="sidebar-btn">Contact Us →</a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
